import type { IconName } from '../utils/icons'

export const brandName = 'Kit INGLES LISTO™'
export const price = '$17 USD'
export const fullPrice = '$171 USD'

export type Stat = {
  value: number
  prefix?: string
  suffix?: string
  label: string
}

export const differenceStats: Stat[] = [
  { value: 12, label: 'Volúmenes curriculares por nivel de progresión' },
  { value: 5, label: 'Bonos de élite incluidos hoy sin costo' },
  { value: 100, suffix: '%', label: 'Descarga inmediata tras el pago' },
]

export const marqueeItems = [
  'Descarga instantánea',
  'Pago único de US$17',
  'Acceso de por vida',
  'Garantía de 7 días',
  'Niveles A1 → B1+',
  '+1.500 docentes',
]

export type LevelTrack = {
  range: string
  title: string
  tomos: string[]
  text: string
}

export const levelTracks: LevelTrack[] = [
  {
    range: 'Tomos 1 al 6',
    title: 'Niveles Iniciales',
    tomos: ['1', '2', '3', '4', '5', '6'],
    text: 'Enfoque en alfabetización inicial, vocabulario visual y estructuras básicas. Ideal para los primeros años de escolaridad.',
  },
  {
    range: 'Tomos 7 al 12',
    title: 'Niveles Intermedios',
    tomos: ['7', '8', '9', '10', '11', '12'],
    text: 'Progresión hacia la autonomía lingüística (A1 a B1+). Perfecto para los años de transición y niveles superiores.',
  },
]

export type ScaffoldingRow = {
  stage: string
  equivalence: string
  english: string
  spanish: string
  englishPct: number
  focus: string
}

export const scaffoldingRows: ScaffoldingRow[] = [
  {
    stage: 'Niveles 1-3',
    equivalence: 'Inicial / Básico I',
    english: '60% - 80%',
    spanish: '40% - 20%',
    englishPct: 70,
    focus: 'Sensibilización y contacto',
  },
  {
    stage: 'Niveles 4-6',
    equivalence: 'Básico II / Consolidación',
    english: '85% - 95%',
    spanish: '15% - 5%',
    englishPct: 90,
    focus: 'Comunicación y autonomía',
  },
  {
    stage: 'Niveles 7-9',
    equivalence: 'Intermedio I (A1/A2)',
    english: '95% - 98%',
    spanish: '5% - 2%',
    englishPct: 96,
    focus: 'Estructuras y fluidez',
  },
  {
    stage: 'Niveles 10-12',
    equivalence: 'Intermedio II (B1/B1+)',
    english: '100%',
    spanish: '0%',
    englishPct: 100,
    focus: 'Inmersión total',
  },
]

export type ComparisonRow = {
  beforeTitle: string
  beforeText: string
  afterTitle: string
  afterText: string
}

export const comparisonRows: ComparisonRow[] = [
  {
    beforeTitle: 'Horas robadas',
    beforeText: 'Fines de semana perdidos buscando recursos en Pinterest.',
    afterTitle: 'Seguridad total',
    afterText: '5 minutos de lectura y entras al aula con el plan resuelto.',
  },
  {
    beforeTitle: 'Caos en el aula',
    beforeText: 'Actividades sueltas, sin conexión pedagógica entre sí.',
    afterTitle: 'Progresión real',
    afterText: 'Material alineado nivel por nivel, del A1 al B1+.',
  },
  {
    beforeTitle: 'Cero estructura',
    beforeText: 'Lecciones aisladas, sin un hilo conductor durante el año.',
    afterTitle: 'Planificación anual',
    afterText: 'Una secuencia didáctica fluida y coherente de principio a fin.',
  },
  {
    beforeTitle: 'Burocracia infinita',
    beforeText: 'Estrés cada vez que tocan reportes, rúbricas o informes.',
    afterTitle: 'Evaluación express',
    afterText: 'Grillas, tests y rúbricas listos al 100% para aplicar.',
  },
  {
    beforeTitle: 'Estudiantes dispersos',
    beforeText: 'Se aburren con material anticuado y fotocopias borrosas.',
    afterTitle: 'Motivación extrema',
    afterText: 'Diseño visual moderno que los mantiene activos y atentos.',
  },
]

export type PackItem = {
  icon: IconName
  title: string
  text: string
}

export const packItems: PackItem[] = [
  {
    icon: 'bookOpen',
    title: '12 Volúmenes de Planificación',
    text: 'Una ruta coherente para todos tus grupos, del Lesson Plan del docente al Student’s Worksheet final.',
  },
  {
    icon: 'printer',
    title: 'Fichas de Trabajo «Ready-to-Print»',
    text: 'Actividades con el balance exacto de lengua meta para cada nivel de competencia.',
  },
  {
    icon: 'languages',
    title: 'Guía del Docente (en Español)',
    text: 'Instrucciones detalladas y Teacher Tips para ejecutar cada clase sin traducir nada sobre la marcha.',
  },
  {
    icon: 'keyRound',
    title: 'Banco de Solucionarios',
    text: 'Todas las respuestas incluidas para hacer correcciones instantáneas frente al grupo.',
  },
  {
    icon: 'clipboardCheck',
    title: 'Instrumentos de Evaluación',
    text: 'Exámenes de unidad y rúbricas analíticas listas para usar en cualquier institución.',
  },
]

export type PageSample = {
  src: string
  level: string
  title: string
  caption: string
}

export const pageSamples: PageSample[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/w1RijJjTnQD8TqjJA7KBr8/Captura-de-pantalla-2026-09-18-a-las-12.35.57_a._m..webp',
    level: 'Niveles iniciales · Worksheet',
    title: 'Unit 2 — Classroom Commands',
    caption:
      'Tres actividades encadenadas (match, circle y trace) con la consigna en inglés y el apoyo en español justo debajo, más un Speaking Moment para cerrar la clase hablando.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pDXPY3GKDXYXkRLbvQHDbK/Captura-de-pantalla-2026-09-18-a-las-12.37.24_a._m..webp',
    level: 'Niveles iniciales · Vocabulario',
    title: 'Challenge 1 — Family Vocabulary',
    caption:
      'Desafío de unir fotografías reales con la palabra desordenada. Los íconos de write y look indican la acción sin necesidad de traducir la instrucción.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/8DytKzEZKcgKdTro3vWhW3/Captura-de-pantalla-2026-09-18-a-las-12.38.30_a._m..webp',
    level: 'Niveles intermedios · Preposiciones',
    title: 'Unit 1 Lesson 3 — The Explorer’s Challenge',
    caption:
      'Producción escrita a partir de una escena real, con tarjetas front/back recortables para practicar en parejas y las Professor Notes para guiar la dinámica.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pnkeGeBEuUqTbJFxU2fCbC/Captura-de-pantalla-2026-09-18-a-las-12.40.12_a._m..webp',
    level: 'Niveles intermedios · Speaking',
    title: 'Unit 1 Lesson 3 — A Sporty Interview',
    caption:
      'Modelo de entrevista, Grammar Box con la estructura del día y sentence-starter cards recortables para que cada pareja arme su propio diálogo.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/sjwm8uKgUFbtUo9bPSWqAC/Captura_de_pantalla_2026-09-18_a_las_12.42.27_a.webp',
    level: 'Niveles intermedios · Gramática',
    title: 'Grammar Spotlight — Nationalities & Languages',
    caption:
      'Lámina de referencia con la tabla país / nacionalidad / idioma y el ejemplo modelado, lista para proyectar o imprimir antes de los ejercicios escritos.',
  },
]

export const bonuses = [
  {
    title: 'The Flashcard Master-Pack',
    text: 'Más de 100 flashcards de alto impacto visual con fotografía profesional, para lograr inmersión total sin una sola palabra en español.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/b7601eb7-7c2b-4ab3-bc97-f1f8ea427998.jpg',
  },
  {
    title: 'Banco de Actividades Reforzadas',
    text: 'Fichas de práctica extra para consolidar gramática. Ideal para refuerzo en clase o tareas: nadie se queda atrás.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/a1182cfe-c135-4cac-82d3-db868f8bb76a.jpg',
  },
  {
    title: 'The Teacher’s Classroom Toolkit',
    text: 'Señalética profesional y herramientas de gestión de aula que fomentan la autonomía del estudiante y el orden visual.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/ad4a0fac-3450-4624-bdfe-535cd2e827fb.jpg',
  },
  {
    title: 'Evaluation Mastery & Data Strategy',
    text: 'Rúbricas analíticas y marcos de diagnóstico experto para seguir el progreso real basado en datos.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/fb457782-0176-4e97-874e-e546f391eee7.jpg',
  },
  {
    title: 'The Secondary Engagement Matrix',
    text: 'Proyectos de alto interés para adolescentes: debates, redes sociales y ética. Diseñado para capturar la atención de secundaria.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/178e779f-2a73-410c-b24e-615fc6c93c64.jpg',
  },
]

export const offerRows = [
  { label: 'Kit INGLES LISTO™ (12 Volúmenes Curriculares)', value: '$97 USD' },
  { label: '5 Bonos Estratégicos (Pack Real)', value: '$74 USD' },
]

export const faqs = [
  {
    q: '¿Cómo recibo el material?',
    a: 'Inmediatamente por email en formato PDF premium tras el pago.',
  },
  {
    q: '¿Se adapta a mi país?',
    a: 'Sí. Al estar basado en niveles de competencia y no en grados fijos, puedes elegir el material que mejor encaje con los objetivos pedagógicos de tu institución.',
  },
  {
    q: '¿Es un pago mensual?',
    a: 'No. Es un único pago de $17 USD por el acceso de por vida a los 12 niveles y los bonos.',
  },
  {
    q: '¿Puedo imprimir para todos mis alumnos?',
    a: 'Totalmente. Tienes licencia para imprimir y distribuir a tus grupos sin límite.',
  },
  {
    q: '¿Necesito ser experto en inglés para usarlo?',
    a: 'No. Cada clase incluye la Guía del Docente en español, con las instrucciones paso a paso, el solucionario completo y la proporción exacta de inglés y español que conviene usar en cada nivel.',
  },
  {
    q: '¿Qué medios de pago aceptan?',
    a: 'Tarjeta de crédito y débito, y los medios locales disponibles en tu país. El cobro se hace en tu moneda con la conversión del día y el acceso se habilita apenas se acredita el pago.',
  },
]
